// ---------------------------------------------------------------------------
// YouAreYou (uru) — API Type Definitions
// Derived directly from docs/api.md. Keep in sync with that file.
// ---------------------------------------------------------------------------

// ---- Envelope ----

/** Standard success response wrapper used by most endpoints. */
export interface ApiSuccess<T> {
  status: true;
  data: T;
}

/** Standard error response wrapper. */
export interface ApiError {
  status: false;
  error: string;
  code: number;
}

/** Union of success and error — use for raw fetch typing before unwrapping. */
export type ApiResponse<T> = ApiSuccess<T> | ApiError;

// ---- Phone Normalization ----

export interface NormalizePhoneRequest {
  phone_number: string;
  /** ISO 3166-1 alpha-2, e.g. "US". Optional — server assumes US if omitted. */
  country_code?: string;
}

export interface NormalizePhoneData {
  phone_number: string; // E.164 format
}

// ---- Registration — multi-step phone-verified flow ----

export interface RegisterStartRequest {
  /** E.164 preferred; normalized via /api/phonehub/number/normalize before calling. */
  phone: string;
}

export interface RegisterStartResponse {
  message: string;
}

export interface RegisterVerifyRequest {
  /** Token from the SMS verification link (?token=...). */
  token: string;
}

export interface RegisterVerifyResponse {
  phone_verified: boolean;
  phone: string; // E.164 — the verified phone number
  /**
   * Determines which screen to show next:
   * - "registration_form" — new person, collect first_name/last_name/dob
   * - "dob_confirm"       — existing person, not KYC'd, need DOB + KYC
   * - "already_verified"  — KYC'd via MojoVerify but not on uru, DOB confirm → fast path (no KYC)
   * - "already_on_uru"    — has an active uru account, redirect to login
   */
  next_step: "registration_form" | "dob_confirm" | "already_verified" | "already_on_uru";
  /** Person's name for the "dob_confirm" welcome-back screen. Null for new users. */
  person_name: string | null;
}

export interface RegisterCompleteRequest {
  /** Token from registerVerify — ties this call to the verified phone. */
  token: string;
  /** ISO format: YYYY-MM-DD */
  date_of_birth: string;
  /** Required when next_step was "registration_form". */
  first_name?: string;
  /** Required when next_step was "registration_form". */
  last_name?: string;
  /** Optional — creates an additional business KYC workspace. */
  company_name?: string;
  /** Alternative to company_name. */
  workspace_name?: string;
  /**
   * Pass when registration is triggered from a global connection link.
   * Enables auto-accept of the connection after KYC completes.
   */
  connection_code?: string;
  /**
   * Pass when registration is triggered from a direct connection link.
   * Enables auto-accept of the connection after KYC completes.
   */
  connection_uuid?: string;
  /**
   * Override redirect after KYC. Defaults to https://youareyou.io/auth.
   * Must be in server allowlist.
   */
  registration_complete_url?: string;
}

export interface RegisterCompleteResponse {
  message?: string;
  /**
   * - "kyc_started" — new/unverified user: KYC link sent via SMS
   * - "complete"    — already KYC'd: user fully provisioned, redirect to redirect_url
   */
  next_step: "kyc_started" | "kyc_redirect" | "complete";
  /**
   * Present when next_step === "complete". Frontend should redirect here immediately.
   * User authenticates via passkey or SMS OTP at this URL.
   */
  redirect_url?: string;
  /**
   * Present when next_step === "kyc_started" (Issue 4 — pending backend).
   * If provided, redirect the user directly to MojoVerify KYC — no second SMS needed.
   */
  kyc_url?: string;
  /**
   * Present when next_step === "kyc_redirect". Direct URL to MojoVerify KYC page.
   * Frontend should redirect to this URL immediately — no SMS needed.
   */
  verification_url?: string;
}

// ---- Connection Recipient Phone Verification ----

export interface ConnectStartRequest {
  /** E.164 phone — normalized before sending. */
  phone: string;
  /** For global links (/v/:code). */
  connection_code?: string;
  /** For direct links (/c/:uuid). */
  connection_uuid?: string;
  /**
   * Optional last name. Stored on the PhoneVerification record and used
   * during connect/verify to issue an ml_token for direct login.
   */
  last_name?: string;
}

export interface ConnectVerifyRequest {
  /** Token from the SMS verification link (?token=...). */
  token: string;
}

export interface ConnectVerifyResponse {
  phone_verified: boolean;
  phone: string;
  /**
   * - "already_verified"   — KYC'd → show Accept/Deny (check has_uru_account for login vs fast-path)
   * - "needs_kyc"          — existing GlobalPerson but not KYC'd → DOB confirm + KYC
   * - "needs_registration" — brand new person → full registration form
   */
  next_step: "already_verified" | "needs_kyc" | "needs_registration";
  person_name: string | null;
  /** Only present when next_step === "already_verified". If true, user needs to login first. */
  has_uru_account?: boolean;
  /**
   * Magic login token (ml:...). Present when next_step === "already_verified",
   * has_uru_account === true, and last_name validation passed.
   * Exchange via POST /api/auth/magic/login to get JWT tokens.
   */
  ml_token?: string;
}

// ---- Authentication — Magic Link ----

export interface MagicLoginRequest {
  /** The `ml:...` token from the SMS link. */
  token: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  /** Access token TTL in seconds (typically 21600 = 6h). */
  expires_in: number;
}

export interface AuthUser {
  id: number;
  username: string; // E.164 phone
  display_name: string;
}

export interface AuthLoginData extends AuthTokens {
  user: AuthUser;
}

// ---- Authentication — Passkey (WebAuthn) ----

export interface PasskeyLoginBeginRequest {
  username: string; // E.164 phone
}

export interface PasskeyPublicKeyOptions {
  rpId: string;
  challenge: string; // base64url
  allowCredentials?: PasskeyCredentialDescriptor[];
  timeout?: number;
  userVerification?: "preferred" | "required" | "discouraged";
}

export interface PasskeyCredentialDescriptor {
  id: string; // base64url
  type: "public-key";
  transports?: AuthenticatorTransport[];
}

export interface PasskeyLoginBeginData {
  challenge_id: string;
  publicKey: PasskeyPublicKeyOptions;
  expiresAt: string; // ISO 8601
}

export interface PasskeyLoginCompleteRequest {
  challenge_id: string;
  credential: PasskeyCredentialResponse;
}

export interface PasskeyCredentialResponse {
  id: string; // base64url
  rawId: string; // base64url
  type: "public-key";
  response: {
    clientDataJSON: string; // base64url
    authenticatorData: string; // base64url
    signature: string; // base64url
    userHandle: string | null; // base64url or null
  };
}

// ---- Passkey Registration (after first login, requires JWT) ----

export interface PasskeyRegisterBeginData {
  challenge_id: string;
  publicKey: PublicKeyCredentialCreationOptionsJSON;
}

/**
 * Simplified shape of the publicKey options returned by register/begin.
 * The actual shape is determined by the server; this covers the fields
 * we need to pass to navigator.credentials.create().
 */
export interface PublicKeyCredentialCreationOptionsJSON {
  rp: { name: string; id: string };
  user: { id: string; name: string; displayName: string };
  challenge: string; // base64url
  pubKeyCredParams: Array<{ type: "public-key"; alg: number }>;
  timeout?: number;
  attestation?: AttestationConveyancePreference;
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  excludeCredentials?: PasskeyCredentialDescriptor[];
}

export interface PasskeyRegisterCompleteRequest {
  challenge_id: string;
  friendly_name: string;
  credential: {
    id: string; // base64url
    rawId: string; // base64url
    type: "public-key";
    response: {
      clientDataJSON: string; // base64url
      attestationObject: string; // base64url
    };
    transports?: AuthenticatorTransport[];
  };
}

export interface PasskeyInfo {
  id: number;
  friendly_name: string;
}

// ---- Authentication — SMS OTP ----

export interface SmsLoginRequest {
  username: string; // E.164 phone
}

export interface SmsVerifyRequest {
  username: string; // E.164 phone
  code: string; // 6-digit
}

// ---- Token Refresh ----

export interface RefreshTokenRequest {
  refresh_token: string;
}

export type RefreshTokenData = AuthTokens;

// ---- Current User ----

export interface UserOrg {
  id: number;
  name: string;
}

export interface UserProfile {
  id: number;
  display_name: string;
  first_name: string;
  last_name: string;
  username: string; // E.164 phone
  email: string; // synthetic placeholder until real email added
  phone_number: string; // E.164
  is_email_verified: boolean;
  is_phone_verified: boolean;
  permissions: Record<string, unknown>;
  metadata: Record<string, unknown>;
  is_active: boolean;
  /** URL string on GET; full AvatarFile object after POST update. Use resolveAvatarUrl(). */
  avatar: string | AvatarFile | null;
  org: UserOrg;
}

// ---- File Object (returned by backend when avatar is expanded) ----
// After a POST update, the backend returns avatar as a full file object.
// On GET /api/user/me it may return a plain URL string or null.

export interface AvatarFile {
  id: number;
  filename: string;
  content_type: string;
  category: string;
  url: string;
  thumbnail: string | null;
}

/** Extract a URL string from whatever shape the backend returned for an avatar. */
export function resolveAvatarUrl(avatar: string | AvatarFile | null | undefined): string | null {
  if (!avatar) return null;
  if (typeof avatar === "string") return avatar;
  return avatar.url ?? null;
}

// ---- File Upload ----

export interface FileUploadInitiateData {
  id: number;
  filename: string;
  content_type: string;
  file_size: number;
  upload_url: string;
}

// ---- Connections ----

export type ConnectionStatus = "pending" | "verified" | "denied" | "cancelled";
export type ConnectionSource = "global_link" | "direct_link";
export type ConnectionDirection = "outgoing" | "incoming";

export interface ConnectionPerson {
  id: number;
  uuid: string;
  first_name: string;
  last_name: string;
  phone?: string;
  face_image_url?: string | null;
  avatar?: string | null;
  kyc_level?: number;
  is_gov_id_verified?: boolean;
  is_liveness_verified?: boolean;
  is_phone_verified?: boolean;
  is_email_verified?: boolean;
  is_ssn_verified?: boolean;
  is_ofac_verified?: boolean;
  is_address_verified?: boolean;
  is_adverse_media_verified?: boolean;
}

/** Config body for POST /api/uru/connections/create */
export interface CreateDirectLinkConfig {
  require_gov_id?: boolean;
  require_liveness?: boolean;
  require_phone?: boolean;
  require_email?: boolean;
  require_ssn?: boolean;
  require_ofac?: boolean;
  require_address?: boolean;
  require_signature?: boolean;
  require_dl_back?: boolean;
  require_adverse_media?: boolean;
  liveness_check_first?: boolean;
  /** Raw recipient input — phone, email, or name. Backend sends SMS/email if detected. */
  recipient?: string;
}

export interface Connection {
  id: number;
  uuid: string;
  status: ConnectionStatus;
  source: ConnectionSource;
  /** "outgoing" = you shared the link, "incoming" = someone shared with you. */
  direction: ConnectionDirection;
  /** True for direct (single-use) links; false for global link connections. */
  single_use?: boolean;
  created: number; // Unix timestamp
  modified?: number; // Unix timestamp
  /** Shareable URL — only populated for outgoing direct links (source=direct_link, direction=outgoing). */
  link?: string;
  denial_reason?: string | null;
  /** KYC requirements stored at creation time. Only returned on detail endpoint. */
  config?: Record<string, boolean>;
  /** Set if the link was sent server-side. "sms" = text sent, "email" = email sent. */
  sent_via?: "sms" | "email";
  /** Label for this connection (person name, phone, or email). */
  label?: string;
  /** The person on the other end of this connection. Null for pending outgoing links. */
  connected_customer: ConnectionPerson | null;
  /** What I share with this connection. Only present on verified connections (detail endpoint). */
  my_sharing?: MySharingState;
  /** What this connection shares with me. Only present on verified connections (detail endpoint). */
  their_sharing?: TheirSharingState;
}

/** Query params for GET /api/uru/connections */
export interface ConnectionsQueryParams {
  status?: ConnectionStatus;
  source?: ConnectionSource;
  direction?: ConnectionDirection;
  search?: string;
  sort?: string;
  start?: number;
  size?: number;
}

// ---- Connection Actions ----
// NOTE: These endpoints return the Connection dict DIRECTLY — not wrapped
// in { status: true, data: ... }. The API client must handle this exception.

export interface AcceptConnectionAction {
  accept: true;
}

export interface DenyConnectionAction {
  deny: {
    reason?: string;
  };
}

export interface CancelConnectionAction {
  cancel: true;
}

export type ConnectionAction =
  | AcceptConnectionAction
  | DenyConnectionAction
  | CancelConnectionAction
  | SharingRequestAction
  | SharingGrantAction
  | SharingRevokeAction;

// ---- Data Sharing ----

export type SharableField =
  | "phone" | "email" | "ssn" | "gov_id" | "address"
  | "liveness" | "ofac" | "adverse_media";

export type SharingStatus =
  | "not_requested"
  | "pending"
  | "active"
  | "expired"
  | "revoked";

export type SharingMode = "share" | "show_valid";

/** Entry in `my_sharing` — what I share with this connection. */
export interface MySharingEntry {
  status: SharingStatus;
  mode?: SharingMode;
  granted_at?: string;
  expires_at?: string | null;
}

/** Entry in `their_sharing` — what they share with me. */
export interface TheirSharingEntry {
  status: SharingStatus;
  mode?: SharingMode;
  /** Actual PII value — only present when status=active and mode=share. */
  value?: string | Record<string, string>;
  expires_at?: string | null;
}

export type MySharingState = Partial<Record<SharableField, MySharingEntry | null>>;
export type TheirSharingState = Partial<Record<SharableField, TheirSharingEntry | null>>;

/** Request access to a field from the other person. */
export interface SharingRequestAction {
  sharing_request: { field: SharableField };
}

/** Grant access to your own data field. */
export interface SharingGrantAction {
  sharing_grant: {
    field: SharableField;
    mode: SharingMode;
    /** 1–30 days. Defaults to 5 on the server. */
    expires_days?: number;
  };
}

/** Revoke access — either what I share out (outbound) or relinquish what I was given (inbound). */
export interface SharingRevokeAction {
  sharing_revoke: {
    field: SharableField;
    direction: "outbound" | "inbound";
  };
}

// ---- Resolve Connection Link ----

export interface RequestorVerifications {
  kyc_level: number;
  is_gov_id_verified: boolean;
  is_liveness_verified: boolean;
  is_phone_verified: boolean;
  is_email_verified: boolean;
  is_ssn_verified: boolean;
  is_ofac_verified: boolean;
  is_address_verified: boolean;
  is_adverse_media_verified: boolean;
}

export interface RecipientInfo {
  phone?: string;
  phone_verified?: boolean;
  clicked_at?: string;
  /** Verified PhoneVerification token — pass to register/complete to skip phone step. */
  pv_token?: string;
  /** Routing info — present when phone_verified is true. Same semantics as connect/verify. */
  next_step?: "already_verified" | "needs_kyc" | "needs_registration";
  person_name?: string | null;
  has_uru_account?: boolean;
  /** Magic login token — present when recipient has uru account and phone matches. */
  ml_token?: string;
}

export interface ResolveConnectionData {
  requestor_name: string;
  requestor_face_url: string | null;
  /** Profile avatar of the requestor (preferred over requestor_face_url which is a liveness scan). */
  requestor_avatar?: string | null;
  connection_code: string;
  /** Non-null for direct links (/c/:uuid). Null for global links (/v/:code). */
  connection_uuid: string | null;
  /** Recipient contact info from connection config (direct links only). */
  recipient_info?: RecipientInfo | null;
  requestor_verifications: RequestorVerifications;
  /**
   * Fields the requestor requires the recipient to verify.
   * e.g. ["gov_id", "liveness", "phone", "ssn", "address", "email", "ofac", "adverse_media"]
   * Present on both global and direct links (BUG-004 fix).
   */
  required_fields?: string[];
  /**
   * Per-field data sharing settings configured on the requestor's link.
   * Determines whether the recipient will share the actual value ("share") or
   * just a verified badge ("show_valid") for each field.
   * Returned by the resolve endpoint (BUG-009 fix).
   */
  field_settings?: FieldSettings;
}

// ---- URU Me (uru-specific profile with connection code + KYC scorecard) ----

export interface UruKyc {
  kyc_level: number;
  risk_level: string;
  confidence_score: number;
  is_gov_id_verified: boolean;
  is_liveness_verified: boolean;
  is_ssn_verified: boolean;
  is_phone_verified: boolean;
  is_email_verified: boolean;
  is_address_verified: boolean;
  is_ofac_verified: boolean;
  is_adverse_media_verified: boolean;
}

export interface UruMeUser {
  id: number;
  display_name: string;
  first_name: string;
  last_name: string;
  username: string; // E.164 phone
  email: string;
  phone_number: string;
  is_phone_verified: boolean;
  is_email_verified: boolean;
  /** URL string on GET; full AvatarFile object after POST update. Use resolveAvatarUrl(). */
  avatar: string | AvatarFile | null;
  /**
   * Whether this user appears in Reality Check searches.
   * true = searchable (default). false = hidden from all searches.
   * Added by ISSUE-023 — treat as true if absent (field not yet deployed).
   */
  reality_check_searchable?: boolean;
}

export interface UruMeData {
  user: UruMeUser;
  connection_code: string;
  /** Full URL — e.g. https://youareyou.io/v/j4Kx9mQ */
  global_link: string;
  /** Default KYC requirements applied when someone connects via the global link. */
  connection_config: Record<string, boolean>;
  kyc: UruKyc;
}

// ---- KYC Status Check ----

export type VerificationStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "failed";

export type RiskLevel = "low" | "medium" | "high";

export interface KycStatusData {
  verification_status: VerificationStatus;
  is_ssn_verified: boolean;
  is_ofac_verified: boolean;
  is_phone_verified: boolean;
  is_email_verified: boolean;
  is_gov_id_verified: boolean;
  is_liveness_verified: boolean;
  risk_level: RiskLevel;
  confidence_score: number;
  kyc_level: number;
  receipt_link: string;

  // ---- Real fields returned by the live endpoint (docs/api.md is out of date) ----
  // The deployed `check-status` returns the full PVR serialization. The canonical
  // status lives in `status` (e.g. "collecting_basic_info" | "collecting_custom_data"
  // | "completed" | "failed"); `verification_status` above is NOT actually present.
  // Treat `completed_at != null` as the authoritative "done" signal.
  status?: string;
  completed_at?: number | null;
  basic_info_completed_at?: number | null;
  uuid?: string;
  basic_info_link?: string;
  custom_form_link?: string;
  trust_level?: string;
  failure_reason?: string | null;
}

// ---- Self-Service KYC ----

export interface SelfKycCreateRequest {
  // Check selection flags
  require_ssn?: boolean;
  require_ofac?: boolean;
  require_address?: boolean;
  require_email?: boolean;
  require_gov_id?: boolean;
  require_liveness?: boolean;
  // Inline data for data-input checks
  ssn?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string; // YYYY-MM-DD
  address_line1?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  email?: string;
  /** Required for data-input checks. */
  consented_to_terms?: boolean;
}

export type SelfKycNextStep =
  | "completed"
  | "gov_id_required"
  | "liveness_required"
  | "pending";

export interface SelfKycCreateResponse {
  uuid: string;
  pvr_status: "pending" | "completed" | "failed";
  next_step: SelfKycNextStep;
  /** Present when interactive checks are required. */
  verification_url?: string;
  /** Present when data-input checks completed successfully. */
  results?: Record<string, Record<string, unknown>>;
  /** True when a pending request already existed — resume instead of creating new. */
  existing?: boolean;
}

export interface SelfKycStatusResponse {
  uuid: string;
  pvr_status: "pending" | "in_progress" | "completed" | "failed";
  next_step: SelfKycNextStep;
  kyc_level?: number;
  risk_level?: RiskLevel;
  /** Present when interactive check is still pending. */
  verification_url?: string;
}

// ---- Global Link Config ----

export type FieldShareMode = "hide" | "show_valid" | "share";
export type FieldAskMode = "dont_ask" | "ask";

export interface FieldSetting {
  share: FieldShareMode;
  ask: FieldAskMode;
}

export type FieldSettings = Partial<Record<SharableField, FieldSetting>>;

export interface GlobalLinkConfigRequest {
  require_gov_id?: boolean;
  require_liveness?: boolean;
  require_phone?: boolean;
  require_email?: boolean;
  require_ssn?: boolean;
  require_ofac?: boolean;
  require_address?: boolean;
  require_signature?: boolean;
  require_dl_back?: boolean;
  require_adverse_media?: boolean;
  liveness_check_first?: boolean;
  field_settings?: FieldSettings;
  /** Opt-out of Reality Check searches. ISSUE-024. */
  reality_check_searchable?: boolean;
  /** Enable/disable the public Verified Human profile page at /p/:code. */
  public_profile_enabled?: boolean;
}

// ---- Notifications ----

export interface AppNotification {
  id: number;
  created: string; // ISO date string
  title: string;
  body: string;
  kind: string; // "general" | "message" | "alert" | "reminder" | free-form
  data: Record<string, unknown>;
  action_url: string | null;
  is_unread: boolean;
  expires_at: string | null;
}

export interface NotificationsQueryParams {
  is_unread?: boolean;
  kind?: string;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// DocSign — Public document-signing recipient flow
//
// These types back the public `/sign/:uuid` recipient page. The signing
// request UUID acts as a bearer token; the four endpoints below require no
// authentication. The sender side (templates, placements, request creation)
// lives in the MojoVerify Admin Portal and is intentionally NOT modeled here.
//
// Backend contract: GET/POST /api/docsign/sign/{uuid}/{document|submit|status|
// existing-signatures}. See docs/docsign-feature-plan.md.
// ---------------------------------------------------------------------------

export type DocSignFieldType = "text" | "date" | "signature";

export type DocSignFieldDataSource =
  | "customer"
  | "custom_form"
  | "auto"
  | "signer_input";

/** A single overlay field on the template PDF, with its pre-resolved value. */
export interface DocSignField {
  id: number;
  field_key: string;
  label: string;
  field_type: DocSignFieldType;
  data_source?: DocSignFieldDataSource;
  data_key?: string;
  /** 1-based page this field sits on. */
  page_number: number;
  /** Position in PDF points — origin bottom-left, y increases upward. */
  x: number;
  y: number;
  width: number;
  height: number;
  is_required: boolean;
  font_size?: number;
  sort_order?: number;
  /** Pre-filled value for text/date fields; empty string for signatures. */
  value: string;
}

/** GET /api/docsign/sign/{uuid}/document */
export interface DocSignDocumentData {
  /** Relative, IP-bound, time-limited path: /api/filevault/file/download/{token}. */
  pdf_url: string;
  page_count: number;
  page_width: number;
  page_height: number;
  template_name: string;
  fields: DocSignField[];
}

export type DocSignStatus =
  | "pending"
  | "viewed"
  | "identity_required"
  | "signing"
  | "completed"
  | "expired"
  | "cancelled";

/** GET /api/docsign/sign/{uuid}/status */
export interface DocSignStatusData {
  uuid: string;
  request_status: DocSignStatus;
  is_expired: boolean;
  require_liveness: boolean;
  liveness_verified: boolean;
  viewed_at: string | null;
  signed_at: string | null;
  completed_at: string | null;
}

/** One reusable signature the signer has on file. */
export interface ExistingSignature {
  uuid: string;
  is_default: boolean;
  created: string | null;
  file_url: string | null;
  source: string;
}

/** GET /api/docsign/sign/{uuid}/existing-signatures */
export interface ExistingSignaturesData {
  has_signatures: boolean;
  signatures: ExistingSignature[];
}

/** A single captured stroke point (or a `{break:true}` pen-lift marker). */
export type SignaturePoint =
  | { x: number; y: number; t: number; existing_uuid?: string }
  | { break: true };

/** POST /api/docsign/sign/{uuid}/submit */
export interface SubmitSignatureRequest {
  /** Base64 PNG — data URL or raw base64. */
  signature_png: string;
  /** Stroke points for replay/forensics. Required, must be non-empty. */
  points: SignaturePoint[];
  strokes?: number;
  source?: string;
  meta?: Record<string, unknown>;
}

export interface SubmitSignatureData {
  doc_sign_request_uuid: string;
  signature_uuid: string;
  signature_event_uuid: string;
  signed_vault_file_id: number;
  status: "completed";
}

/**
 * String error codes returned in the `error`/`code` envelope by the public
 * signing endpoints (note: `code` here is a string, unlike the numeric `code`
 * on the standard ApiError envelope). Detected in the UI primarily via HTTP
 * status, with these as documentation of the possible cases.
 */
export type DocSignErrorCode =
  | "NOT_FOUND"
  | "EXPIRED"
  | "ALREADY_COMPLETED"
  | "CANCELLED"
  | "LIVENESS_REQUIRED"
  | "NO_TEMPLATE"
  | "MISSING_SIGNATURE"
  | "MISSING_POINTS"
  | "SIGNING_ERROR"
  | "INTERNAL_ERROR";

// ---- DocSign — portal/admin read models (for the signed-in "Documents" page) ----
// These hit the existing admin docsign endpoints (the logged-in uru user is also
// a portal admin = same account.User), so no new backend endpoint is required.
// Shapes are intentionally permissive — the list graph nests partial refs.

export interface DocSignCustomerRef {
  id?: number;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

export interface DocSignTemplateRef {
  id?: number;
  name?: string | null;
  version?: number;
}

/** A signing request row from GET /api/docsign/request. */
export interface DocSignRequestSummary {
  id: number;
  uuid: string;
  status: DocSignStatus;
  require_liveness?: boolean;
  created?: number | string | null;
  expires_at?: number | string | null;
  completed_at?: number | string | null;
  signed_vault_file?: number | { id: number } | null;
  customer?: DocSignCustomerRef | number | null;
  template?: DocSignTemplateRef | number | null;
}

/** A group the user belongs to (GET /api/group). */
export interface AccountGroup {
  id: number;
  name?: string;
  kind?: string | null;
  parent?: { id: number; name?: string } | number | null;
}

/** A template row from GET /api/docsign/template. */
export interface DocumentTemplateSummary {
  id: number;
  uuid: string;
  name: string;
  description?: string | null;
  file_id?: number | null;
  page_count?: number;
  is_default?: boolean;
  is_active?: boolean;
  version?: number;
  created?: number | string | null;
}
